import { METERS_PER_UNIT, MILS_PER_CIRCLE } from "./units.ts";

/**
 * A position in in-game map coordinates.
 *
 * These are the same numbers a player reads off the in-game map screen:
 * one unit equals 100 m. `+y` points north (towards the top of the tactical
 * map), which is the convention the in-game UI uses.
 */
export interface GameCoord {
  readonly x: number;
  readonly y: number;
}

/** An axis-aligned rectangle in game coordinates. */
export interface Bounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

export function coord(x: number, y: number): GameCoord {
  return { x, y };
}

/** Straight-line distance between two points, in meters. */
export function distanceMeters(a: GameCoord, b: GameCoord): number {
  return Math.hypot(b.x - a.x, b.y - a.y) * METERS_PER_UNIT;
}

/** Straight-line distance between two points, in game units. */
export function distanceUnits(a: GameCoord, b: GameCoord): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Compass bearing from `from` to `to`, in degrees.
 *
 * 0 = North, 90 = East, 180 = South, 270 = West. Always in [0, 360).
 * Returns 0 when the two points coincide.
 */
export function bearingDegrees(from: GameCoord, to: GameCoord): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === 0) return 0;
  const deg = (Math.atan2(dx, dy) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/** Compass bearing in NATO mils (6400 per circle). Always in [0, 6400). */
export function bearingMils(from: GameCoord, to: GameCoord): number {
  return (bearingDegrees(from, to) * MILS_PER_CIRCLE) / 360;
}

export function degreesToMils(degrees: number): number {
  return (degrees * MILS_PER_CIRCLE) / 360;
}

export function milsToDegrees(mils: number): number {
  return (mils * 360) / MILS_PER_CIRCLE;
}

/**
 * The reciprocal of a bearing — the direction you would fly to come back.
 * Useful for briefing an egress heading alongside an ingress heading.
 */
export function reciprocalDegrees(degrees: number): number {
  return (degrees + 180) % 360;
}

/**
 * Shortest signed turn from one heading to another, in degrees.
 * Negative means turn left, positive means turn right. Range (-180, 180].
 */
export function headingDeltaDegrees(from: number, to: number): number {
  const delta = ((to - from + 540) % 360) - 180;
  // Normalise -180 to +180 so a reversal always reads as a right turn.
  return delta === -180 ? 180 : delta;
}

/** The 16-point compass label for a bearing, e.g. "NNE". */
export function compassPoint(degrees: number): string {
  const points = [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ] as const;
  const index = Math.round((((degrees % 360) + 360) % 360) / 22.5) % 16;
  return points[index] ?? "N";
}

/** Move `meters` from `origin` along a compass bearing. */
export function project(origin: GameCoord, bearingDeg: number, meters: number): GameCoord {
  const rad = (bearingDeg * Math.PI) / 180;
  const units = meters / METERS_PER_UNIT;
  return {
    x: origin.x + Math.sin(rad) * units,
    y: origin.y + Math.cos(rad) * units,
  };
}

/** Linear interpolation between two coordinates. `t` is clamped to [0, 1]. */
export function lerp(a: GameCoord, b: GameCoord, t: number): GameCoord {
  const clamped = Math.min(1, Math.max(0, t));
  return {
    x: a.x + (b.x - a.x) * clamped,
    y: a.y + (b.y - a.y) * clamped,
  };
}

/** Midpoint between two coordinates. */
export function midpoint(a: GameCoord, b: GameCoord): GameCoord {
  return lerp(a, b, 0.5);
}

export function isWithinBounds(point: GameCoord, bounds: Bounds): boolean {
  return (
    point.x >= bounds.minX &&
    point.x <= bounds.maxX &&
    point.y >= bounds.minY &&
    point.y <= bounds.maxY
  );
}

export function clampToBounds(point: GameCoord, bounds: Bounds): GameCoord {
  return {
    x: Math.min(bounds.maxX, Math.max(bounds.minX, point.x)),
    y: Math.min(bounds.maxY, Math.max(bounds.minY, point.y)),
  };
}

export function boundsWidth(bounds: Bounds): number {
  return bounds.maxX - bounds.minX;
}

export function boundsHeight(bounds: Bounds): number {
  return bounds.maxY - bounds.minY;
}

export function boundsCenter(bounds: Bounds): GameCoord {
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
}

/**
 * Format a coordinate the way the in-game screen and the community tools do,
 * e.g. `x105.05, y109.14`. This exact shape is what players paste to each other.
 */
export function formatCoord(point: GameCoord, decimals = 2): string {
  return `x${point.x.toFixed(decimals)}, y${point.y.toFixed(decimals)}`;
}

/**
 * Parse a coordinate from human input.
 *
 * Accepts labelled (`x105.05 y109.14`, `X105,05 Y109,14`) and bare
 * (`105.05 109.14`, `105.05, 109.14`) forms, with either decimal separator.
 * Returns `undefined` when no sensible pair can be read.
 */
export function parseCoord(input: string): GameCoord | undefined {
  const cleaned = input.trim().toLowerCase();
  if (cleaned.length === 0) return undefined;

  const labelled = /x\s*(-?\d+(?:[.,]\d+)?)\D+y\s*(-?\d+(?:[.,]\d+)?)/.exec(cleaned);
  if (labelled?.[1] !== undefined && labelled[2] !== undefined) {
    return buildCoord(labelled[1], labelled[2]);
  }

  const numbers = cleaned.match(/-?\d+(?:[.,]\d+)?/g);
  if (numbers && numbers.length >= 2 && numbers[0] !== undefined && numbers[1] !== undefined) {
    return buildCoord(numbers[0], numbers[1]);
  }

  return undefined;
}

function buildCoord(rawX: string, rawY: string): GameCoord | undefined {
  const x = Number(rawX.replace(",", "."));
  const y = Number(rawY.replace(",", "."));
  if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
  return { x, y };
}

/** Format a distance for a readout: meters under 1 km, otherwise kilometers. */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

/** Format a bearing as a zero-padded three-digit heading, e.g. `072°`. */
export function formatBearing(degrees: number): string {
  const normalised = Math.round(((degrees % 360) + 360) % 360) % 360;
  return `${normalised.toString().padStart(3, "0")}°`;
}
